import React from 'react';
import './ProjectDetail.css'; // Assuming you'll reuse the same CSS
import {
  ProjectFullWidth,
  ProjectSideBySide,
  ProjectSideBySideReverse,
  ProjectFooter
} from './ProjectCommon';

function RootsProjectDetail() {
  const footerProps = {
    title: "Rooted in Place",
    description: "A collaborative project exploring sustainable agriculture and community development.",
    collaborators: [
      {
        name: "Hana Khurshid",
        url: "https://www.linkedin.com/in/hana-khurshid/"
      },
      {
        name: "Isabelle Lee",
        url: "https://www.linkedin.com/in/isabelle-lee-cy/"
      }
    ],
    backgroundImage: "/rooted_in_place/lattice_closeup.webp"
  };

  return (
    <div className="project-detail-root">
      <div className="project-detail-hero">
        <img src="/rooted_in_place/hero.webp" alt="Rooted in Place - Living Architecture" className="project-detail-hero-img" />
      </div>
      <div className="project-detail-content">
        <div className="project-detail-intro">
          <div className="project-detail-title">
            <h1>Rooted in Place: Building with Living Systems</h1>
            <p className="project-credit">
              A collaborative project by Luke Fiorante, <a href="https://www.linkedin.com/in/hana-khurshid/" target="_blank" rel="noopener noreferrer" className="project-link">Hana Khurshid</a>, and <a href="https://www.linkedin.com/in/isabelle-lee-cy/" target="_blank" rel="noopener noreferrer" className="project-link">Isabelle Lee</a>
            </p>
          </div>
          <p>
            <b>Rooted in Place</b> is a speculative architectural experiment that explores the potential of integrating living systems into our built environment. It investigates how technology, biology, and architecture can converge, using 3D-printed scaffolds to guide plant roots in forming self-supporting, living structures. In an age where urban settings often disconnect us from the natural world, this project represents a small step towards reimagining cities as places where structures don't just occupy land but actively grow, adapt, and contribute to their surrounding ecosystems.
          </p>
        </div>
        <div className="project-detail-process">
          <ProjectFullWidth image="/rooted_in_place/process.webp" alt="Experimentation process">
            <h2>Motivation</h2>
            <p>
              Roots are one of nature's most fascinating and intelligent living systems. Hidden beneath the surface, they form intricate networks that sense, adapt, communicate, and respond to their environment. They can navigate obstacles, source water and nutrients, and even engage in chemical communication with other organisms. Our primary goal was to make these incredible, yet often overlooked, systems visible, fostering an appreciation for their vital role and exploring a symbiotic relationship where human-made structures and plant life can grow and thrive in partnership.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/rooted_in_place/lattices.webp" alt="3D printed lattice tiles for root growth" caption="3D-printed scaffolds designed to guide root growth.">
            <h2>Designing for Growth: 3D Printed Scaffolds</h2>
            <p>
              To observe and guide the behavior of root systems up close, we designed and fabricated a series of 3D-printed lattices. These structures act as scaffolds, providing channels and pathways for roots to grow on, around, and within. We explored various design parameters, including pattern porosity and density, as well as the height and vertical spacing of the lattice layers. This iterative design process allowed us to create templates that could potentially direct root growth to form stable, interwoven matrices.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/rooted_in_place/lattice_roots.webp" alt="Close-up of wheatgrass roots intertwining with a 3D printed tile" caption="Wheatgrass roots binding 3D-printed tiles into a living mat.">
            <h2>Experimentation and Observation</h2>
            <p>
              Our core experimentation involved cultivating mung beans and wheatgrass within these 3D-printed frameworks, using an agar solution as a growth medium. We observed how the roots interacted with the different tile designs, noting their adaptability and their remarkable ability to intertwine and bind the separate components together. For instance, wheatgrass, with its fibrous root system, showed particular promise in creating a dense, interconnected mat, successfully joining multiple tiles. These experiments highlighted the potential for roots to replace traditional binding agents in creating self-supporting living walls.
            </p>
          </ProjectSideBySideReverse>

          <ProjectFullWidth image="/rooted_in_place/systems_map.webp" alt="Animated Systems Map for Rooted in Place" caption="A systems map tracing the journey from biosphere to living construction.">
            <h2>A Systems Perspective</h2>
            <p>
              Understanding the broader context of our work was crucial. We developed a comprehensive systems map to illustrate the interconnected relationships between natural ecosystems, conventional material production, and the potential of plant-centric architecture. This map traces the journey from the biosphere and natural material systems, through the challenges of conventional joinery and waste generation, to an unfolding future where sustainable technologies and ecological symbiosis could lead to living construction. This systemic view positions "Rooted in Place" not as an isolated experiment, but as a node within a larger technological and ecological arc.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/rooted_in_place/future_vision.webp" alt="Vision of a re-wilded urban environment with living structures" caption="A vision for rewilded cities, where buildings and nature thrive together.">
            <h2>A Regenerative Horizon</h2>
            <p>
              "Rooted in Place" is more than an exploration of novel building materials; it's a step towards reimagining how we inhabit our cities. We envision a future where urban landscapes are rewilded, where buildings actively participate in the ecosystem, fostering biodiversity and creating healthier environments. While a small-scale experiment, this project aims to spark new possibilities, suggesting that by building in partnership with living systems, we can create architecture that is both grown and constructed, leading to cities where humans and nature thrive together.
            </p>
          </ProjectSideBySide>
        </div>

      </div>
      <ProjectFooter {...footerProps} />
    </div>
  );
}

export default RootsProjectDetail;
